import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import GlobalNav from "../components/GlobalNav";
import Footer from "../components/Footer";
import { deltaToHtml } from "../utils/quillDelta";
import "./BlogPost.css";

// Real /blog/:id route, replacing the old /blog#id hash-anchor that
// pointed at a page which never actually existed. Fetches the full
// /api/blogs list and finds the matching post client-side -- there's no
// dedicated single-post endpoint confirmed to exist yet; fine at this
// content volume, worth a real GET /api/blogs/:id later if the list
// grows large enough that this becomes wasteful.
function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [id]);

  async function fetchPost() {
    try {
      const res = await fetch('/api/blogs');
      const data = await res.json();
      const found = (data.blogs || []).find((b) => String(b.id) === String(id));
      if (found) setPost(found);
      else setNotFound(true);
    } catch (err) {
      console.error('Error fetching post:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }

  async function submitComment() {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/blogs/${id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment }),
      });
      if (res.ok) {
        setComment("");
        fetchPost(); // refresh to show the new comment
      }
    } catch (err) {
      console.error('Error posting comment:', err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="blogpost-page">
      <GlobalNav activeHref="/" ctaLabel="Join the collective" />

      <div className="blogpost-body">
        {loading && <p className="blogpost-loading">Loading…</p>}

        {!loading && notFound && (
          <p className="blogpost-empty">This post doesn't exist, or may have been removed.</p>
        )}

        {!loading && post && (
          <div className="blogpost-card">
            <div className="blogpost-header">
              <div className="blogpost-avatar">
                {post.userphoto ? (
                  <img src={post.userphoto} alt={post.author} />
                ) : (
                  <span>{(post.author || '?')[0]}</span>
                )}
              </div>
              <div>
                <div className="blogpost-author">{post.author}</div>
                <div className="blogpost-date">posted on {formatDate(post.date)}</div>
              </div>
            </div>

            <h1 className="blogpost-title">{post.title}</h1>

            <div className="blogpost-description" dangerouslySetInnerHTML={{ __html: deltaToHtml(post.description) }} />

            {post.photourl && (
              <div className="blogpost-image">
                <img src={post.photourl} alt={post.title} />
              </div>
            )}

            {(post.reference1_link || post.reference2_link) && (
              <div className="blogpost-links">
                <div className="blogpost-links-label">Links</div>
                {post.reference1_link && (
                  <a href={post.reference1_link} target="_blank" rel="noopener noreferrer">
                    {post.reference1_text || post.reference1_link}
                  </a>
                )}
                {post.reference2_link && (
                  <a href={post.reference2_link} target="_blank" rel="noopener noreferrer">
                    {post.reference2_text || post.reference2_link}
                  </a>
                )}
              </div>
            )}

            <div className="blogpost-divider" />

            <div className="blogpost-comment-form">
              <label>Add a comment</label>
              <textarea
                rows={3}
                placeholder="Comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <button
                className="mihu-btn-primary-lg"
                onClick={submitComment}
                disabled={submitting || !comment.trim()}
              >
                {submitting ? 'Posting…' : 'Submit'}
              </button>
            </div>

            {post.comments && post.comments.length > 0 && (
              <>
                <div className="blogpost-divider" />
                <div className="blogpost-comments">
                  {post.comments.map((c, idx) => (
                    <div key={idx} className="blogpost-comment">
                      <div className="blogpost-comment-avatar small">
                        {c.photo ? <img src={c.photo} alt={c.firstName} /> : <span>{(c.firstName || '?')[0]}</span>}
                      </div>
                      <div>
                        <div className="blogpost-comment-name">{c.firstName}</div>
                        <div className="blogpost-comment-date">commented on {formatDate(c.date)}</div>
                        <p className="blogpost-comment-text">"{c.comment}"</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}